import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { BadgeCheck, Loader2, ShieldOff } from "lucide-react";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function ManageBadgeModal({ open, onClose }) {
  const { refresh } = useAuth();
  const [cancelling, setCancelling] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => (await api.get("/subscription")).data,
    enabled: open,
  });

  const sub = data?.subscription;

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post("/subscription/cancel");
      await refresh();
      toast.success("Verification cancelled");
      onClose();
      setConfirm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="manage-badge-modal" className="bg-card border-2 border-[#6C0BA9] rounded-3xl max-w-md">
        <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
          <BadgeCheck className="w-6 h-6 text-[#C084FC]" /> Manage Verification
        </DialogTitle>
        {isLoading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-[#8012C6] animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-2xl border border-border bg-secondary/50 p-4 space-y-2.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Plan</span>
                <span className="font-semibold">Xealth Verified</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Price</span>
                <span className="font-semibold">{sub?.amount || "$4.00"} / {sub?.interval || "month"}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Status</span>
                <span className="font-semibold capitalize inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-400" />{sub?.status || "active"}
                </span>
              </div>
              {sub?.current_period_end && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Renews</span>
                  <span className="font-semibold">{format(new Date(sub.current_period_end), "MMM d, yyyy")}</span>
                </div>
              )}
            </div>

            {!confirm ? (
              <Button data-testid="cancel-sub-btn" onClick={() => setConfirm(true)} variant="outline" className="w-full rounded-xl border-red-500/40 text-red-400 hover:bg-red-500/10 gap-2">
                <ShieldOff className="w-4 h-4" /> Cancel Subscription
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-center text-muted-foreground">Your badge will be removed immediately. Are you sure?</p>
                <div className="flex gap-2">
                  <Button data-testid="cancel-sub-abort-btn" onClick={() => setConfirm(false)} variant="outline" className="flex-1 rounded-xl border-border">Keep it</Button>
                  <Button data-testid="cancel-sub-confirm-btn" onClick={cancel} disabled={cancelling} className="flex-1 rounded-xl bg-red-500 hover:bg-red-600 text-white gap-2">
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Yes, cancel"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
